import { motion, useReducedMotion } from 'framer-motion';

/**
 * GraphDB + GraphRAG 비주얼.
 * 엔티티 타입별 색상(안건·부서·문서 = 삼분 그래프 느낌)의 지식 그래프 위로
 * 검색 흐름을 나타내는 "탐색 코멧"이 노드를 따라 이동하며 경로를 점등한다.
 */

type NType = 'topic' | 'dept' | 'doc';
interface GNode {
  x: number;
  y: number;
  label: string;
  type: NType;
}

const NODES: GNode[] = [
  { x: 300, y: 60, label: '회의', type: 'topic' }, // 0
  { x: 392, y: 132, label: '안건 A', type: 'topic' }, // 1
  { x: 338, y: 250, label: '안건 B', type: 'topic' }, // 2
  { x: 452, y: 330, label: '결정', type: 'topic' }, // 3
  { x: 168, y: 150, label: '기획부', type: 'dept' }, // 4
  { x: 150, y: 300, label: '예산부', type: 'dept' }, // 5
  { x: 250, y: 408, label: '운영부', type: 'dept' }, // 6
  { x: 568, y: 120, label: '회의록', type: 'doc' }, // 7
  { x: 612, y: 250, label: '보고서', type: 'doc' }, // 8
  { x: 560, y: 380, label: '집행계획', type: 'doc' }, // 9
];

const EDGES: Array<[number, number]> = [
  [0, 1],
  [0, 2],
  [1, 2],
  [2, 3],
  [1, 7],
  [2, 8],
  [3, 9],
  [1, 4],
  [2, 5],
  [3, 6],
  [0, 4],
  [3, 8],
  [2, 6],
  [1, 8],
];

// 검색 흐름 경로: 회의 → 안건A → 회의록 → 안건B → 결정 → 예산부
const PATH = [0, 1, 7, 2, 3, 5];

const TYPE_COLOR: Record<NType, string> = {
  topic: '#9a7bff',
  dept: '#2ad6e6',
  doc: '#e0455a',
};

export default function GraphRagDiagram() {
  const reduce = useReducedMotion();
  const loop = [...PATH, PATH[0]];
  const cx = loop.map((i) => NODES[i].x);
  const cy = loop.map((i) => NODES[i].y);
  const period = 6.6;
  const visitDelay = (rank: number) => (period / PATH.length) * rank;

  return (
    <div className="ai-diagram">
      <svg viewBox="0 0 760 460" role="img" aria-label="GraphDB · GraphRAG 지식 그래프 탐색">
        {/* 엣지 */}
        {EDGES.map(([a, b], i) => (
          <line key={i} className="ai-graph-edge" x1={NODES[a].x} y1={NODES[a].y} x2={NODES[b].x} y2={NODES[b].y} />
        ))}

        {/* 노드 */}
        {NODES.map((n, i) => {
          const rank = PATH.indexOf(i);
          const onPath = rank >= 0;
          return (
            <g key={i} className={`ai-gnode is-${n.type} ${onPath ? 'is-path' : ''}`}>
              <circle
                cx={n.x}
                cy={n.y}
                r={onPath ? 22 : 17}
                stroke={TYPE_COLOR[n.type]}
                style={onPath ? { animationDelay: `${visitDelay(rank)}s` } : undefined}
              />
              <text x={n.x} y={n.y + 1} textAnchor="middle" dominantBaseline="middle" fontSize={10}>
                {n.label}
              </text>
            </g>
          );
        })}

        {/* 탐색 코멧 */}
        {reduce ? (
          <circle className="ai-graph-comet" r={6} cx={cx[0]} cy={cy[0]} />
        ) : (
          <motion.circle
            className="ai-graph-comet"
            r={6}
            initial={{ cx: cx[0], cy: cy[0] }}
            animate={{ cx, cy }}
            transition={{
              duration: period,
              times: loop.map((_, i) => i / (loop.length - 1)),
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}
      </svg>

      <div className="ai-graph-legend">
        <span className="is-topic">안건</span>
        <span className="is-dept">부서</span>
        <span className="is-doc">문서</span>
      </div>
      <div className="ai-diagram__caption">엔티티 관계를 따라 탐색하며 다단계 질의에 설명 가능한 근거 제공</div>
    </div>
  );
}
